import type {
  DocumentServicesObject,
  PageRef,
  RouterRef,
} from '@wix/document-services-types';

// Error codes for router prefix validation (mirrors InvalidPrefixReason in host-integration)
const DUPLICATE_ERROR_CODES = [3, 6]; // PREFIX_IS_DUPLICATE_OF_URI_SEO, PREFIX_IS_IN_USE_BY_ANOTHER_APPLICATION
const INVALID_CHAR_ERROR_CODE = 4; // PREFIX_CONTAINS_INVALID_CHARACTERS

const ILLEGAL_CHARACTERS_PATTERN = /[^\p{L}\p{M}0-9-]/gu;
const MAX_SUFFIX_ATTEMPTS = 9;

const sanitizeRouterPrefix = (prefix: string): string =>
  prefix.replace(ILLEGAL_CHARACTERS_PATTERN, '');

const isDuplicatePrefixError = (errorCode: number | string): boolean =>
  DUPLICATE_ERROR_CODES.includes(Number(errorCode));

const isInvalidCharError = (errorCode: number | string): boolean =>
  Number(errorCode) === INVALID_CHAR_ERROR_CODE;

function resolveRouterPrefix(
  documentServices: DocumentServicesObject,
  appDefinitionId: string,
  preferredPrefix: string,
  initialErrorCode: number | string,
): string | undefined {
  if (
    !isInvalidCharError(initialErrorCode) &&
    !isDuplicatePrefixError(initialErrorCode)
  ) {
    return undefined;
  }

  const candidate = isInvalidCharError(initialErrorCode)
    ? sanitizeRouterPrefix(preferredPrefix)
    : preferredPrefix;

  if (!candidate) {
    return undefined;
  }

  const check = documentServices.routers.isValidPrefixByAppDefId(
    appDefinitionId,
    candidate,
  );
  if (check.valid) {
    return candidate;
  }

  if (!isDuplicatePrefixError(check.errorCode)) {
    return undefined;
  }

  for (let i = 1; i <= MAX_SUFFIX_ATTEMPTS; i++) {
    const suffixed = `${candidate}-${i}`;
    const result = documentServices.routers.isValidPrefixByAppDefId(
      appDefinitionId,
      suffixed,
    );
    if (result.valid) {
      return suffixed;
    }
    if (!isDuplicatePrefixError(result.errorCode)) {
      return undefined;
    }
  }

  return undefined;
}

// TODO: [EP-8700] Consolidate with addRouter + ensureValidRouterPrefix in editor-platform-host-integration
export async function addRouterExtension(
  documentServices: DocumentServicesObject,
  {
    appDefinitionId,
    extensionId,
    preferredPrefix,
    pagesToConnect,
  }: {
    appDefinitionId: string;
    extensionId: string;
    preferredPrefix: string;
    pagesToConnect: { pageRef: PageRef; pageRole: string }[];
  },
): Promise<RouterRef> {
  const { valid, errorCode } = documentServices.routers.isValidPrefixByAppDefId(
    appDefinitionId,
    preferredPrefix,
  );

  let prefix: string;
  if (valid) {
    prefix = preferredPrefix;
  } else {
    const resolved = resolveRouterPrefix(
      documentServices,
      appDefinitionId,
      preferredPrefix,
      errorCode,
    );
    if (!resolved) {
      throw new Error(
        `Failed to add router: could not resolve a valid prefix for "${preferredPrefix}"`,
      );
    }
    prefix = resolved;
  }

  const routerRef = await documentServices.routers.add({
    appDefinitionId,
    extensionId,
    config: '',
    prefix,
  });

  for (const { pageRef, pageRole } of pagesToConnect) {
    await documentServices.routers.pages.connect(
      routerRef,
      pageRef,
      [pageRole],
      {},
    );
  }

  return routerRef;
}
